import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from, Observable } from 'rxjs';
import { ParentService } from '../../../core/services/parent.service';
import { ParentActions } from './parent.actions';
import {
  IParentListFilter,
  IParentListResponse,
  IParentListSort,
  IParentSingleResponse,
} from '../../models/parent.model';
import { Router } from '@angular/router';
import { Store,Action } from '@ngrx/store';
import { getParentQueries, selectParent } from './parent.selectors';
import { AlertService } from 'src/app/core/services/alert.service';
import { ClientActions } from '../client/client.actions';

// Helper function to handle API calls
function handleApiCall<T extends { message?: string }>(
  apiCall: Promise<T>,
  successAction: (response: T) => Action,
  failureAction: (error: { error: string }) => Action,
  alertService?: AlertService
): Observable<Action> {
  return from(apiCall).pipe(
    map((response) => {
      if (alertService && response.message) {
        alertService.success(
          response.message,
          'Success',
        );
      }
      return successAction(response);
    }),
    catchError((error) => {
      return of(failureAction({ error: error.message }));
    })
  );
}

@Injectable()
export class ParentEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store,
    private readonly parentService: ParentService,
    private readonly alertService: AlertService
  ) {}

  loadParentsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.loadParentsList),
      withLatestFrom(this.store.select(getParentQueries)),
      mergeMap(([, filters]) => {
        const filter: IParentListFilter = filters.body?.filter ?? {};
        const sort: IParentListSort = filters.body?.sort ?? {
          key: 'createdAt',
          order: 'desc',
        };
        return handleApiCall(
          this.parentService.getParents(
            filters.params?.page,
            filters.params?.limit,
            filter,
            sort
          ),
          (parents: IParentListResponse) =>
            ParentActions.loadParentsListSuccess({
              response: parents,
              filter: {
                params: filters.params,
                body: { filter, sort },
              },
            }),
          ParentActions.loadParentsListFailure
        );
      })
    )
  );

  // For loading single parent
  loadSingleParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.loadSingleParent),
      mergeMap(({ id }) =>
        handleApiCall(
          this.parentService.getParent(id),
          (response: IParentSingleResponse) =>
            ParentActions.loadSingleParentSuccess({ parent: response.data }),
          ParentActions.loadSingleParentFailure
        )
      )
    )
  );

  // For select parent
  selectParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.selectParent),
      mergeMap(({ id, withRedirect = false }) =>
        handleApiCall(
          this.parentService.getParent(id),
          (response: IParentSingleResponse) =>
            ParentActions.selectParentSuccess({
              parent: response.data,
              withRedirect,
            }),
          ParentActions.selectParentFailure
        )
      )
    )
  );

  // After update, re-fetch the single parent via selectParent to ensure freshest server state.
  // Guard: only dispatch if parent._id exists to avoid 404 on empty id.
  redirectAfterSingleUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.updateParentSuccess),
      map(({ parent }) => {
        if (parent && parent._id) {
          return ParentActions.selectParent({ id: parent._id });
        }
        // Fallback: no id, just clear currentParent.
        return ParentActions.clearCurrentParent();
      }),
      catchError((error) => {
        console.error('Error in redirect effect (post-update refetch):', error);
        return of(ParentActions.loadSingleParentFailure({ error }));
      })
    )
  );

  redirectAfterSelectParent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParentActions.selectParentSuccess),
        tap(({ withRedirect }) => {
          if (withRedirect) {
            this.router.navigate(['/client']);
          }
        })
      ),
    { dispatch: false }
  );

  createParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.createParent),
      mergeMap((action) =>
        from(this.parentService.createParent(action.parent)).pipe(
          map((res: IParentSingleResponse) => {
            this.alertService.success(
              res.message,
              'Success',
              `${res.data.client_name} `
            );
            action.closeModalSignal.set(true);
            return ParentActions.createParentSuccess({ parent: res.data });
          }),
          catchError((error) =>
            of(ParentActions.createParentFailure({ error: error.message }))
          )
        )
      )
    )
  );

  updateParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.updateParent),
      mergeMap((action) =>
        from(this.parentService.updateParent(action.id, action.parent)).pipe(
          map((res: IParentSingleResponse) => {
            this.alertService.success(
              res.message,
              'Success',
              `${res.data.client_name} `
            );
            action.closeModalSignal.set(true);
            return ParentActions.updateParentSuccess({ parent: res.data });
          }),
          catchError((error) =>
            of(ParentActions.updateParentFailure({ error: error.message }))
          )
        )
      )
    )
  );

  redirectAfterCreateUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ParentActions.createParentSuccess,
          ParentActions.updateParentSuccess,
          ParentActions.deleteParentSuccess,
          ParentActions.changeParentStatusSuccess // on loadlist -> selectuser will set null hence removed: temp- TBD
        ),
        withLatestFrom(
          this.store.select(getParentQueries),
          this.store.select(selectParent) // Read one more value from selectParent
        ),
        mergeMap(([action,, selectedParent]) => {
          const parentId = action.type === ParentActions.changeParentStatusSuccess.type ? action.id : selectedParent?._id ?? '';
          const actionsToDispatch: Array<Action> = [
            ParentActions.clearCurrentParent(),
            ParentActions.loadParentsList(),
          ];
          if (action.type === ParentActions.changeParentStatusSuccess.type) {
            actionsToDispatch.push(ParentActions.selectParent({ id: parentId }))
          }
          if (action.type === ParentActions.deleteParentSuccess.type) {
            actionsToDispatch.push(ParentActions.clearSelectedParent())
          }
          return actionsToDispatch;
        })
      )
  );


  // to handle when user navigates from sidebar client to parent list
  // redirectFromSidebar$ = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(ParentActions.loadParentsListSuccess),
  //       map(() => {
  //           return ParentActions.clearCurrentParent();
  //       }),
  //     )
  // );

  deleteParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.deleteParent),
      mergeMap((action) =>
        handleApiCall(
          this.parentService.deleteParent(action.id),
          () => {
            action.closeModalSignal.set(true);
            return ParentActions.deleteParentSuccess({ id: action.id });
          },
          ParentActions.deleteParentFailure,
          this.alertService
        )
      )
    )
  );

  // Redirect after delete parent from client page
  redirectAfterDeleteParent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParentActions.deleteParentSuccess),
        tap(() => {
          this.router.navigate(['/business-unit']);
        })
      ),
    { dispatch: false }
  );

  changeParentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParentActions.changeParentStatus),
      mergeMap((action) =>
        handleApiCall(
          this.parentService.changeParentStatus(action.id),
          () => ParentActions.changeParentStatusSuccess({ id: action.id, fetchClients: action.fetchClients }),
          ParentActions.changeParentStatusFailure,
          this.alertService
        )
      )
    )
  );

  // this is to handle when parent status is changed from client page, then we need to
  // fetch client list again to reflect the changes in client list this is not required when parent status is changed from parent page
  // as parent page already has client list updated and we are not using this effect in parent page
  changeParentStatusFromClient1$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParentActions.changeParentStatusSuccess),
        tap(({ fetchClients }) => {
          if (fetchClients) {
            this.store.dispatch(ClientActions.listClients());
          }
        })
      ),
    { dispatch: false }
  );
}
